/**
 * Sampling & Selection section for the Test Suite Configuration page.
 *
 * Contains 10 subsections covering metrics selection, dimensions selection,
 * column mapping, frequency weight columns, selection settings, time period
 * generation, parameter output format, schema required defaults, parameter
 * name mapping, and LLM polish settings.
 *
 * @see Requirements 10.1–10.11
 */

import React, { useState } from 'react';
import {
  ExpandableSection,
  SpaceBetween,
  FormField,
  Input,
  Toggle,
  Button,
  Header,
  Container,
  ColumnLayout,
  TokenGroup,
} from '@amzn/awsui-components-react';
import { ConfigPageAction, ConfigPageState, ValidationErrors } from './types';

// ─── Props ─────────────────────────────────────────────────────────────

export interface SamplingSectionProps {
  state: ConfigPageState;
  dispatch: React.Dispatch<ConfigPageAction>;
  validationErrors: ValidationErrors;
}

// ─── Reusable Sub-component: Editable Tag List (TokenGroup + Input) ───

interface EditableTagListProps {
  items: string[];
  onUpdate: (updated: string[]) => void;
  placeholder?: string;
  ariaLabel: string;
}

const EditableTagList: React.FC<EditableTagListProps> = ({
  items,
  onUpdate,
  placeholder = 'Type a value and press Enter',
  ariaLabel,
}) => {
  const [inputValue, setInputValue] = useState('');

  const handleKeyDown = (key: string) => {
    if (key === 'Enter' && inputValue.trim()) {
      const trimmed = inputValue.trim();
      if (!items.includes(trimmed)) {
        onUpdate([...items, trimmed]);
      }
      setInputValue('');
    }
  };

  const handleDismiss = (itemIndex: number) => {
    onUpdate(items.filter((_, i) => i !== itemIndex));
  };

  return (
    <SpaceBetween size="xs">
      <Input
        value={inputValue}
        onChange={({ detail }) => setInputValue(detail.value)}
        onKeyDown={({ detail }) => handleKeyDown(detail.key)}
        placeholder={placeholder}
        ariaLabel={ariaLabel}
      />
      {items.length > 0 && (
        <TokenGroup
          items={items.map((item) => ({
            label: item,
            dismissLabel: `Remove ${item}`,
          }))}
          onDismiss={({ detail }) => handleDismiss(detail.itemIndex)}
        />
      )}
    </SpaceBetween>
  );
};

// ─── Reusable Sub-component: Key-Value Editor ──────────────────────────

interface KeyValueEditorProps {
  entries: Record<string, unknown>;
  onUpdate: (updated: Record<string, unknown>) => void;
  keyLabel?: string;
  valueLabel?: string;
  keyReadOnly?: boolean;
  ariaLabelPrefix: string;
}

const KeyValueEditor: React.FC<KeyValueEditorProps> = ({
  entries,
  onUpdate,
  keyLabel = 'Key',
  valueLabel = 'Value',
  keyReadOnly = false,
  ariaLabelPrefix,
}) => {
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');
  const [editingKeys, setEditingKeys] = useState<Record<string, string>>({});

  const entryList = Object.entries(entries);

  const serializeValue = (value: unknown): string => {
    if (typeof value === 'string') return value;
    return JSON.stringify(value);
  };

  const parseValue = (rawValue: string): unknown => {
    try {
      return JSON.parse(rawValue);
    } catch {
      return rawValue;
    }
  };

  const handleValueChange = (key: string, rawValue: string) => {
    onUpdate({ ...entries, [key]: parseValue(rawValue) });
  };

  const handleKeyBlur = (originalKey: string) => {
    const edited = editingKeys[originalKey];
    if (edited !== undefined && edited !== originalKey) {
      const updated = { ...entries };
      const value = updated[originalKey];
      delete updated[originalKey];
      updated[edited] = value;
      onUpdate(updated);
    }
    setEditingKeys(({ [originalKey]: _, ...rest }) => rest);
  };

  const handleKeyKeyDown = (originalKey: string, pressedKey: string) => {
    if (pressedKey === 'Enter') {
      handleKeyBlur(originalKey);
    }
  };

  const handleRemove = (key: string) => {
    const updated = { ...entries };
    delete updated[key];
    onUpdate(updated);
    // Clean up any in-progress edit for the removed key
    setEditingKeys(({ [key]: _, ...rest }) => rest);
  };

  const handleAdd = () => {
    if (newKey.trim()) {
      onUpdate({ ...entries, [newKey.trim()]: parseValue(newValue) });
      setNewKey('');
      setNewValue('');
    }
  };

  const handleAddKeyDown = (key: string) => {
    if (key === 'Enter') {
      handleAdd();
    }
  };

  return (
    <SpaceBetween size="s">
      {entryList.map(([key, value]) => (
        <SpaceBetween key={key} size="xs" direction="horizontal">
          <FormField label={keyLabel}>
            <Input
              value={editingKeys[key] ?? key}
              readOnly={keyReadOnly}
              disabled={keyReadOnly}
              ariaLabel={`${ariaLabelPrefix} key ${key}`}
              onChange={({ detail }) => {
                if (!keyReadOnly) {
                  setEditingKeys((prev) => ({ ...prev, [key]: detail.value }));
                }
              }}
              onBlur={() => {
                if (!keyReadOnly) {
                  handleKeyBlur(key);
                }
              }}
              onKeyDown={({ detail }) => {
                if (!keyReadOnly) {
                  handleKeyKeyDown(key, detail.key);
                }
              }}
            />
          </FormField>
          <FormField label={valueLabel}>
            <Input
              value={serializeValue(value)}
              onChange={({ detail }) => handleValueChange(key, detail.value)}
              ariaLabel={`${ariaLabelPrefix} value for ${key}`}
            />
          </FormField>
          <FormField label=" ">
            <Button
              iconName="remove"
              variant="icon"
              onClick={() => handleRemove(key)}
              ariaLabel={`Remove ${ariaLabelPrefix} entry ${key}`}
            />
          </FormField>
        </SpaceBetween>
      ))}

      {/* Add new entry row */}
      <SpaceBetween size="xs" direction="horizontal">
        <FormField label={`New ${keyLabel.toLowerCase()}`}>
          <Input
            value={newKey}
            onChange={({ detail }) => setNewKey(detail.value)}
            onKeyDown={({ detail }) => handleAddKeyDown(detail.key)}
            placeholder={`Enter ${keyLabel.toLowerCase()}`}
            ariaLabel={`New ${ariaLabelPrefix} key`}
          />
        </FormField>
        <FormField label={`New ${valueLabel.toLowerCase()}`}>
          <Input
            value={newValue}
            onChange={({ detail }) => setNewValue(detail.value)}
            onKeyDown={({ detail }) => handleAddKeyDown(detail.key)}
            placeholder={`Enter ${valueLabel.toLowerCase()}`}
            ariaLabel={`New ${ariaLabelPrefix} value`}
          />
        </FormField>
        <FormField label=" ">
          <Button iconName="add-plus" onClick={handleAdd} ariaLabel={`Add ${ariaLabelPrefix} entry`}>
            Add
          </Button>
        </FormField>
      </SpaceBetween>
    </SpaceBetween>
  );
};

// ─── Sub-component: Criteria Params Editor ─────────────────────────────

interface CriteriaParamsEditorProps {
  criteriaParams: Record<string, { enabled: boolean; default_criteria: string }>;
  onUpdate: (updated: Record<string, { enabled: boolean; default_criteria: string }>) => void;
}

const CriteriaParamsEditor: React.FC<CriteriaParamsEditorProps> = ({
  criteriaParams,
  onUpdate,
}) => {
  const [newKey, setNewKey] = useState('');
  const [newCriteria, setNewCriteria] = useState('');

  const entryList = Object.entries(criteriaParams);

  const handleToggle = (key: string, checked: boolean) => {
    onUpdate({
      ...criteriaParams,
      [key]: { ...criteriaParams[key], enabled: checked },
    });
  };

  const handleCriteriaChange = (key: string, value: string) => {
    onUpdate({
      ...criteriaParams,
      [key]: { ...criteriaParams[key], default_criteria: value },
    });
  };

  const handleRemove = (key: string) => {
    const updated = { ...criteriaParams };
    delete updated[key];
    onUpdate(updated);
  };

  const handleAdd = () => {
    if (newKey.trim()) {
      onUpdate({
        ...criteriaParams,
        [newKey.trim()]: { enabled: true, default_criteria: newCriteria || 'ALL_OF' },
      });
      setNewKey('');
      setNewCriteria('');
    }
  };

  const handleAddKeyDown = (key: string) => {
    if (key === 'Enter') {
      handleAdd();
    }
  };

  return (
    <SpaceBetween size="s">
      {entryList.map(([key, param]) => (
        <SpaceBetween key={key} size="xs" direction="horizontal">
          <FormField label="Parameter">
            <Input
              value={key}
              readOnly
              disabled
              ariaLabel={`Criteria param name ${key}`}
            />
          </FormField>
          <FormField label="Enabled">
            <Toggle
              checked={param.enabled}
              onChange={({ detail }) => handleToggle(key, detail.checked)}
              ariaLabel={`Enable criteria for ${key}`}
            >
              {param.enabled ? 'On' : 'Off'}
            </Toggle>
          </FormField>
          <FormField label="Default criteria">
            <Input
              value={param.default_criteria}
              onChange={({ detail }) => handleCriteriaChange(key, detail.value)}
              placeholder="e.g. ALL_OF"
              ariaLabel={`Default criteria for ${key}`}
            />
          </FormField>
          <FormField label=" ">
            <Button
              iconName="remove"
              variant="icon"
              onClick={() => handleRemove(key)}
              ariaLabel={`Remove criteria param ${key}`}
            />
          </FormField>
        </SpaceBetween>
      ))}

      {/* Add new criteria param row */}
      <SpaceBetween size="xs" direction="horizontal">
        <FormField label="New parameter">
          <Input
            value={newKey}
            onChange={({ detail }) => setNewKey(detail.value)}
            onKeyDown={({ detail }) => handleAddKeyDown(detail.key)}
            placeholder="Parameter name"
            ariaLabel="New criteria param name"
          />
        </FormField>
        <FormField label="New criteria">
          <Input
            value={newCriteria}
            onChange={({ detail }) => setNewCriteria(detail.value)}
            onKeyDown={({ detail }) => handleAddKeyDown(detail.key)}
            placeholder="e.g. ALL_OF"
            ariaLabel="New criteria param default criteria"
          />
        </FormField>
        <FormField label=" ">
          <Button iconName="add-plus" onClick={handleAdd} ariaLabel="Add criteria param entry">
            Add
          </Button>
        </FormField>
      </SpaceBetween>
    </SpaceBetween>
  );
};

// ─── Component ─────────────────────────────────────────────────────────

const SamplingSection: React.FC<SamplingSectionProps> = ({
  state,
  dispatch,
  validationErrors,
}) => {
  const handleFieldChange = (path: string, value: unknown) => {
    dispatch({ type: 'UPDATE_FIELD', path, value });
  };

  const handleNumberChange = (path: string, rawValue: string) => {
    const parsed = parseFloat(rawValue);
    handleFieldChange(path, isNaN(parsed) ? rawValue : parsed);
  };

  return (
    <ExpandableSection
      headerText="Sampling & Selection"
      variant="container"
      defaultExpanded
    >
      <SpaceBetween size="l">
        {/* ── 1. Metrics Selection ──────────────────────────────────── */}
        <Container header={<Header variant="h3">Metrics Selection</Header>}>
          <SpaceBetween size="s">
            <FormField
              label="Coupled with other params"
              description="Whether metrics are sampled together with other parameters"
            >
              <Toggle
                checked={state.metricsSelection.coupled_with_other_params}
                onChange={({ detail }) =>
                  handleFieldChange(
                    'metricsSelection.coupled_with_other_params',
                    detail.checked,
                  )
                }
                ariaLabel="Metrics coupled with other params"
              >
                {state.metricsSelection.coupled_with_other_params
                  ? 'Coupled'
                  : 'Independent'}
              </Toggle>
            </FormField>

            <FormField
              label="Metric enum values"
              description="Available metric values for sampling"
            >
              <EditableTagList
                items={state.metricsSelection.enum}
                onUpdate={(updated) =>
                  handleFieldChange('metricsSelection.enum', updated)
                }
                placeholder="Type a metric name and press Enter"
                ariaLabel="Add metric enum value"
              />
            </FormField>
          </SpaceBetween>
        </Container>

        {/* ── 2. Dimensions Selection ───────────────────────────────── */}
        <Container header={<Header variant="h3">Dimensions Selection</Header>}>
          <SpaceBetween size="s">
            <FormField
              label="Enabled"
              description="Enable dimension-based sampling"
            >
              <Toggle
                checked={state.dimensionsSelection.enabled}
                onChange={({ detail }) =>
                  handleFieldChange(
                    'dimensionsSelection.enabled',
                    detail.checked,
                  )
                }
                ariaLabel="Enable dimensions selection"
              >
                {state.dimensionsSelection.enabled ? 'Enabled' : 'Disabled'}
              </Toggle>
            </FormField>

            <ColumnLayout columns={2}>
              <FormField
                label="Probability"
                description="Probability of including dimensions in a question"
                errorText={validationErrors['dimensionsSelection.probability']}
                constraintText="Must be between 0.0 and 1.0"
              >
                <Input
                  value={String(state.dimensionsSelection.probability)}
                  onChange={({ detail }) =>
                    handleNumberChange(
                      'dimensionsSelection.probability',
                      detail.value,
                    )
                  }
                  type="number"
                  inputMode="decimal"
                  ariaLabel="Dimensions probability"
                />
              </FormField>

              <SpaceBetween size="s">
                <FormField
                  label="Min values"
                  description="Minimum number of dimension values to include"
                >
                  <Input
                    value={String(state.dimensionsSelection.min_values)}
                    onChange={({ detail }) =>
                      handleNumberChange(
                        'dimensionsSelection.min_values',
                        detail.value,
                      )
                    }
                    type="number"
                    inputMode="numeric"
                    ariaLabel="Dimensions min values"
                  />
                </FormField>

                <FormField
                  label="Max values"
                  description="Maximum number of dimension values to include"
                >
                  <Input
                    value={String(state.dimensionsSelection.max_values)}
                    onChange={({ detail }) =>
                      handleNumberChange(
                        'dimensionsSelection.max_values',
                        detail.value,
                      )
                    }
                    type="number"
                    inputMode="numeric"
                    ariaLabel="Dimensions max values"
                  />
                </FormField>
              </SpaceBetween>
            </ColumnLayout>

            <FormField
              label="Dimension enum values"
              description="Available dimension values for sampling"
            >
              <EditableTagList
                items={state.dimensionsSelection.enum}
                onUpdate={(updated) =>
                  handleFieldChange('dimensionsSelection.enum', updated)
                }
                placeholder="Type a dimension value and press Enter"
                ariaLabel="Add dimension enum value"
              />
            </FormField>
          </SpaceBetween>
        </Container>

        {/* ── 3. Column Mapping ─────────────────────────────────────── */}
        <Container header={<Header variant="h3">Column Mapping</Header>}>
          <ColumnLayout columns={2}>
            <FormField
              label="Metrics column"
              description="Column name for derived metrics availability"
            >
              <Input
                value={state.columnMapping.metrics}
                onChange={({ detail }) =>
                  handleFieldChange('columnMapping.metrics', detail.value)
                }
                placeholder="derived_metrics_available"
                ariaLabel="Column mapping metrics"
              />
            </FormField>

            <FormField
              label="Min date column"
              description="Column name for minimum date range"
            >
              <Input
                value={state.columnMapping.min_date}
                onChange={({ detail }) =>
                  handleFieldChange('columnMapping.min_date', detail.value)
                }
                placeholder="min_{temporal_param}"
                ariaLabel="Column mapping min date"
              />
            </FormField>

            <FormField
              label="Max date column"
              description="Column name for maximum date range"
            >
              <Input
                value={state.columnMapping.max_date}
                onChange={({ detail }) =>
                  handleFieldChange('columnMapping.max_date', detail.value)
                }
                placeholder="max_{temporal_param}"
                ariaLabel="Column mapping max date"
              />
            </FormField>
          </ColumnLayout>
        </Container>

        {/* ── 4. Frequency Weight Columns ───────────────────────────── */}
        <Container header={<Header variant="h3">Frequency Weight Columns</Header>}>
          <FormField
            label="Weight columns"
            description="Columns used for stratified sampling frequency weights"
          >
            <EditableTagList
              items={state.frequencyWeightColumns}
              onUpdate={(updated) =>
                handleFieldChange('frequencyWeightColumns', updated)
              }
              placeholder="Type a column name and press Enter"
              ariaLabel="Add frequency weight column"
            />
          </FormField>
        </Container>

        {/* ── 5. Selection Settings ─────────────────────────────────── */}
        <Container header={<Header variant="h3">Selection Settings</Header>}>
          <ColumnLayout columns={2}>
            <FormField
              label="Questions per template"
              description="Number of questions to generate per template pattern"
            >
              <Input
                value={String(state.selectionSettings.questions_per_template)}
                onChange={({ detail }) =>
                  handleNumberChange(
                    'selectionSettings.questions_per_template',
                    detail.value,
                  )
                }
                type="number"
                inputMode="numeric"
                ariaLabel="Questions per template"
              />
            </FormField>

            <FormField
              label="Diversity enabled"
              description="Enable diversity-based selection to avoid repetitive questions"
            >
              <Toggle
                checked={state.selectionSettings.diversity_enabled}
                onChange={({ detail }) =>
                  handleFieldChange(
                    'selectionSettings.diversity_enabled',
                    detail.checked,
                  )
                }
                ariaLabel="Diversity enabled"
              >
                {state.selectionSettings.diversity_enabled
                  ? 'Enabled'
                  : 'Disabled'}
              </Toggle>
            </FormField>
          </ColumnLayout>
        </Container>

        {/* ── 6. Time Period Generation ──────────────────────────────── */}
        <Container header={<Header variant="h3">Time Period Generation</Header>}>
          <ColumnLayout columns={2}>
            <FormField label="Enable months">
              <Toggle
                checked={state.timePeriodGeneration.enable_months}
                onChange={({ detail }) =>
                  handleFieldChange(
                    'timePeriodGeneration.enable_months',
                    detail.checked,
                  )
                }
                ariaLabel="Enable months"
              >
                {state.timePeriodGeneration.enable_months
                  ? 'Enabled'
                  : 'Disabled'}
              </Toggle>
            </FormField>

            <FormField label="Enable quarters">
              <Toggle
                checked={state.timePeriodGeneration.enable_quarters}
                onChange={({ detail }) =>
                  handleFieldChange(
                    'timePeriodGeneration.enable_quarters',
                    detail.checked,
                  )
                }
                ariaLabel="Enable quarters"
              >
                {state.timePeriodGeneration.enable_quarters
                  ? 'Enabled'
                  : 'Disabled'}
              </Toggle>
            </FormField>

            <FormField label="Enable half years">
              <Toggle
                checked={state.timePeriodGeneration.enable_half_years}
                onChange={({ detail }) =>
                  handleFieldChange(
                    'timePeriodGeneration.enable_half_years',
                    detail.checked,
                  )
                }
                ariaLabel="Enable half years"
              >
                {state.timePeriodGeneration.enable_half_years
                  ? 'Enabled'
                  : 'Disabled'}
              </Toggle>
            </FormField>

            <FormField label="Enable years">
              <Toggle
                checked={state.timePeriodGeneration.enable_years}
                onChange={({ detail }) =>
                  handleFieldChange(
                    'timePeriodGeneration.enable_years',
                    detail.checked,
                  )
                }
                ariaLabel="Enable years"
              >
                {state.timePeriodGeneration.enable_years
                  ? 'Enabled'
                  : 'Disabled'}
              </Toggle>
            </FormField>

            <FormField label="Enable YTD">
              <Toggle
                checked={state.timePeriodGeneration.enable_ytd}
                onChange={({ detail }) =>
                  handleFieldChange(
                    'timePeriodGeneration.enable_ytd',
                    detail.checked,
                  )
                }
                ariaLabel="Enable YTD"
              >
                {state.timePeriodGeneration.enable_ytd
                  ? 'Enabled'
                  : 'Disabled'}
              </Toggle>
            </FormField>

            <FormField label="Enable QTD">
              <Toggle
                checked={state.timePeriodGeneration.enable_qtd}
                onChange={({ detail }) =>
                  handleFieldChange(
                    'timePeriodGeneration.enable_qtd',
                    detail.checked,
                  )
                }
                ariaLabel="Enable QTD"
              >
                {state.timePeriodGeneration.enable_qtd
                  ? 'Enabled'
                  : 'Disabled'}
              </Toggle>
            </FormField>
          </ColumnLayout>
        </Container>

        {/* ── 7. Parameter Output Format ────────────────────────────── */}
        <Container header={<Header variant="h3">Parameter Output Format</Header>}>
          <SpaceBetween size="s">
            <FormField
              label="Ignore patterns"
              description="Parameter name patterns to exclude from output"
            >
              <EditableTagList
                items={state.parameterOutputFormat.ignore_patterns}
                onUpdate={(updated) =>
                  handleFieldChange(
                    'parameterOutputFormat.ignore_patterns',
                    updated,
                  )
                }
                placeholder="Type a pattern and press Enter"
                ariaLabel="Add ignore pattern"
              />
            </FormField>

            <FormField
              label="Criteria params"
              description="Parameters with criteria-based filtering (enabled toggle + default criteria)"
            >
              <CriteriaParamsEditor
                criteriaParams={state.parameterOutputFormat.criteria_params}
                onUpdate={(updated) =>
                  handleFieldChange(
                    'parameterOutputFormat.criteria_params',
                    updated,
                  )
                }
              />
            </FormField>

            <FormField
              label="Apply criteria to all arrays"
              description="Whether to apply criteria filtering to all array-type parameters"
            >
              <Toggle
                checked={state.parameterOutputFormat.apply_criteria_to_all_arrays}
                onChange={({ detail }) =>
                  handleFieldChange(
                    'parameterOutputFormat.apply_criteria_to_all_arrays',
                    detail.checked,
                  )
                }
                ariaLabel="Apply criteria to all arrays"
              >
                {state.parameterOutputFormat.apply_criteria_to_all_arrays
                  ? 'Enabled'
                  : 'Disabled'}
              </Toggle>
            </FormField>
          </SpaceBetween>
        </Container>

        {/* ── 8. Schema Required Defaults ───────────────────────────── */}
        <Container header={<Header variant="h3">Schema Required Defaults</Header>}>
          <FormField
            label="Required parameter defaults"
            description="Default values for required schema parameters (values can be JSON for complex types)"
          >
            <KeyValueEditor
              entries={state.schemaRequiredDefaults}
              onUpdate={(updated) =>
                handleFieldChange('schemaRequiredDefaults', updated)
              }
              keyLabel="Parameter"
              valueLabel="Default value"
              ariaLabelPrefix="Schema required default"
            />
          </FormField>
        </Container>

        {/* ── 9. Parameter Name Mapping ─────────────────────────────── */}
        <Container header={<Header variant="h3">Parameter Name Mapping</Header>}>
          <FormField
            label="Name mappings"
            description="Maps snake_case schema keys to camelCase parameter names"
          >
            <KeyValueEditor
              entries={state.paramNameMapping}
              onUpdate={(updated) =>
                handleFieldChange(
                  'paramNameMapping',
                  updated as Record<string, string>,
                )
              }
              keyLabel="Schema key"
              valueLabel="Parameter name"
              keyReadOnly
              ariaLabelPrefix="Param name mapping"
            />
          </FormField>
        </Container>

        {/* ── 10. LLM Polish ────────────────────────────────────────── */}
        <Container header={<Header variant="h3">LLM Polish</Header>}>
          <SpaceBetween size="s">
            <FormField
              label="Removable values"
              description="Values that the LLM polisher may remove from generated questions"
            >
              <EditableTagList
                items={state.llmPolish.removable_values}
                onUpdate={(updated) =>
                  handleFieldChange('llmPolish.removable_values', updated)
                }
                placeholder="Type a value and press Enter"
                ariaLabel="Add removable value"
              />
            </FormField>

            <FormField
              label="Must preserve"
              description="Values that the LLM polisher must keep in generated questions"
            >
              <EditableTagList
                items={state.llmPolish.must_preserve}
                onUpdate={(updated) =>
                  handleFieldChange('llmPolish.must_preserve', updated)
                }
                placeholder="Type a value and press Enter"
                ariaLabel="Add must preserve value"
              />
            </FormField>
          </SpaceBetween>
        </Container>

        {/* ── 11. OrderBy Selection ─────────────────────────────────── */}
        <Container header={<Header variant="h3">OrderBy Selection</Header>}>
          <SpaceBetween size="s">
            <FormField
              label="Enabled"
              description="Enable random orderBy parameter generation"
            >
              <Toggle
                checked={state.orderBySelection.enabled}
                onChange={({ detail }) =>
                  handleFieldChange('orderBySelection.enabled', detail.checked)
                }
                ariaLabel="Enable orderBy selection"
              >
                {state.orderBySelection.enabled ? 'Enabled' : 'Disabled'}
              </Toggle>
            </FormField>

            <FormField
              label="Probability"
              description="Probability of including orderBy in a generated question"
              errorText={validationErrors['orderBySelection.probability']}
              constraintText="Must be between 0.0 and 1.0"
            >
              <Input
                value={String(state.orderBySelection.probability)}
                onChange={({ detail }) =>
                  handleNumberChange('orderBySelection.probability', detail.value)
                }
                type="number"
                inputMode="decimal"
                ariaLabel="OrderBy selection probability"
              />
            </FormField>
          </SpaceBetween>
        </Container>

        {/* ── 12. Stratified Sampling ───────────────────────────────── */}
        <Container header={<Header variant="h3">Stratified Sampling</Header>}>
          <SpaceBetween size="s">
            <FormField
              label="Enabled"
              description="Enable stratified sampling for balanced row selection"
            >
              <Toggle
                checked={state.stratifiedSampling.enabled}
                onChange={({ detail }) =>
                  handleFieldChange('stratifiedSampling.enabled', detail.checked)
                }
                ariaLabel="Enable stratified sampling"
              >
                {state.stratifiedSampling.enabled ? 'Enabled' : 'Disabled'}
              </Toggle>
            </FormField>

            <FormField
              label="Frequency weight columns"
              description="Columns used for frequency-based sampling weights"
            >
              <EditableTagList
                items={state.stratifiedSampling.frequency_weight_columns}
                onUpdate={(updated) =>
                  handleFieldChange('stratifiedSampling.frequency_weight_columns', updated)
                }
                placeholder="Type a column name and press Enter"
                ariaLabel="Add stratified sampling frequency weight column"
              />
            </FormField>

            <FormField
              label="Stratify columns"
              description="Columns to stratify sampling by"
            >
              <EditableTagList
                items={state.stratifiedSampling.stratify_columns}
                onUpdate={(updated) =>
                  handleFieldChange('stratifiedSampling.stratify_columns', updated)
                }
                placeholder="Type a column name and press Enter"
                ariaLabel="Add stratify column"
              />
            </FormField>

            <FormField
              label="Exclude columns"
              description="Columns to exclude from stratified sampling"
            >
              <EditableTagList
                items={state.stratifiedSampling.exclude_columns}
                onUpdate={(updated) =>
                  handleFieldChange('stratifiedSampling.exclude_columns', updated)
                }
                placeholder="Type a column name and press Enter"
                ariaLabel="Add exclude column"
              />
            </FormField>
          </SpaceBetween>
        </Container>
      </SpaceBetween>
    </ExpandableSection>
  );
};

export default SamplingSection;
